import Tooltip from 'components/layout/tooltip';
import {
  AirHandlerMetrics,
  MetricsRange
} from 'hooks/use-matching-air-handler-metrics';
import { abbreviateNumber } from 'utils/helpers';

type SystemsListMode = 'matching' | 'valid';

type SummaryCountButtonProps = Readonly<{
  label: string;
  value: string | number;
  isRefreshing: boolean;
  tooltip?: string;
  disabled?: boolean;
  onClick: () => void;
}>;

const LIST_NOT_READY_TOOLTIP =
  'The systems list opens once every zone has an air handler and the systems have loaded.';

function SummaryCountButton({
  label,
  value,
  isRefreshing,
  tooltip,
  disabled = false,
  onClick
}: SummaryCountButtonProps) {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
      className={`relative flex flex-1 flex-col items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
        disabled
          ? 'cursor-not-allowed opacity-70'
          : 'cursor-pointer hover:bg-gray-50'
      }`}
    >
      <span className='whitespace-nowrap text-xs font-medium uppercase tracking-wide text-gray-500'>
        {label}
      </span>
      <span className='text-lg font-semibold tabular-nums text-gray-800'>
        {value}
      </span>
      {tooltip !== undefined && <Tooltip text={tooltip} />}
      {isRefreshing && (
        <span
          aria-label='Refreshing'
          title={`Refreshing ${label.toLowerCase()}`}
          className='absolute right-1.5 top-1.5 h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500'
        />
      )}
    </button>
  );
}

type SummaryRangeRowProps = Readonly<{
  label: string;
  range: MetricsRange;
  format: (value: number) => string;
  prefix?: string;
  suffix?: string;
}>;

function SummaryRangeRow({
  label,
  range,
  format,
  prefix = '',
  suffix = ''
}: SummaryRangeRowProps) {
  const value =
    range.min === range.max
      ? `${prefix}${format(range.min)}`
      : `${prefix}${format(range.min)} - ${prefix}${format(range.max)}`;
  return (
    <div className='flex items-center justify-between text-sm'>
      <span className='text-xs font-medium uppercase tracking-wide text-gray-500'>
        {label}
      </span>
      <span className='font-semibold tabular-nums text-gray-800'>
        {value}
        {suffix}
      </span>
    </div>
  );
}

type DuctedSystemsSummaryCardProps = Readonly<{
  validSystemCount: number;
  matchingSystemCount: number | null;
  isRefreshing: boolean;
  metrics: AirHandlerMetrics | null;
  metricsLoading: boolean;
  fallbackPriceRange: MetricsRange | null;
  // The list modal reads the fetched rows; until the full request has
  // settled the counts still show (pool aggregates) but cannot be opened.
  listReady?: boolean;
  onOpenList: (mode: SystemsListMode) => void;
}>;

export default function DuctedSystemsSummaryCard({
  validSystemCount,
  matchingSystemCount,
  isRefreshing,
  metrics,
  metricsLoading,
  fallbackPriceRange,
  listReady = true,
  onOpenList
}: DuctedSystemsSummaryCardProps) {
  const priceRange = metrics?.priceRange ?? fallbackPriceRange;
  const seer2Range = metrics?.seer2Range ?? null;
  const btuRange = metrics?.btuRange ?? null;
  const hasRanges =
    priceRange !== null || seer2Range !== null || btuRange !== null;
  const matchingTooltip =
    matchingSystemCount === null
      ? 'Add at least one air handler per condenser in a zone to filter matching systems.'
      : undefined;

  return (
    <div className='flex shrink-0 flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4'>
      <div className='flex gap-2'>
        <SummaryCountButton
          label='Valid Systems'
          value={validSystemCount}
          isRefreshing={isRefreshing}
          disabled={!listReady}
          tooltip={listReady ? undefined : LIST_NOT_READY_TOOLTIP}
          onClick={() => onOpenList('valid')}
        />
        <SummaryCountButton
          label='Matching Systems'
          value={matchingSystemCount ?? '—'}
          isRefreshing={isRefreshing}
          disabled={!listReady}
          tooltip={
            matchingTooltip ?? (listReady ? undefined : LIST_NOT_READY_TOOLTIP)
          }
          onClick={() => onOpenList('matching')}
        />
      </div>
      {metricsLoading && !metrics && !hasRanges && (
        <div className='text-center text-xs text-gray-400'>Loading...</div>
      )}
      {hasRanges && (
        <div className='flex flex-col gap-1'>
          {priceRange && (
            <SummaryRangeRow
              label='Price'
              range={priceRange}
              format={abbreviateNumber}
              prefix='$'
            />
          )}
          {seer2Range && (
            <SummaryRangeRow
              label='Efficiency'
              range={seer2Range}
              format={(value) => String(Math.round(value))}
              suffix=' SEER'
            />
          )}
          {btuRange && (
            <SummaryRangeRow
              label='Capacity'
              range={btuRange}
              format={abbreviateNumber}
              suffix=' BTU'
            />
          )}
        </div>
      )}
    </div>
  );
}
